/**
 * ============================================================================
 * AUTO-RELEASE COUNTDOWN COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Displays countdown timer for 72-hour auto-release after delivery
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../providers';
import Card from './Card';
import ProgressBar from './ProgressBar';

/**
 * Auto-Release Countdown Component
 * @param {Object} props
 * @param {string} props.deliveredAt - ISO date string when delivery was marked
 * @param {string} props.autoReleaseAt - ISO date string for auto-release (optional, calculated if not provided)
 * @param {Function} props.onExpire - Callback when countdown expires
 * @returns {JSX.Element}
 */
const AutoReleaseCountdown = ({ deliveredAt, autoReleaseAt, onExpire }) => {
  const { language } = useI18n();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!deliveredAt && !autoReleaseAt) return;

    const calculateTimeRemaining = () => {
      let autoReleaseTime;
      if (autoReleaseAt) {
        autoReleaseTime = new Date(autoReleaseAt);
      } else if (deliveredAt) {
        const delivered = new Date(deliveredAt);
        autoReleaseTime = new Date(delivered.getTime() + 72 * 60 * 60 * 1000); // 72 hours
      } else {
        return;
      }

      const now = new Date();
      const diff = autoReleaseTime - now;

      if (diff <= 0) {
        setExpired(true);
        setTimeRemaining(null);
        if (onExpire) onExpire();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [deliveredAt, autoReleaseAt, onExpire]);

  if (!deliveredAt && !autoReleaseAt) return null;

  if (expired) {
    return (
      <Card
        variant="outlined"
        className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 animate-fadeInUp"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-2xl shadow-lg">
            ⏰
          </div>
          <div>
            <p className="font-black text-yellow-800 dark:text-yellow-200 text-lg">
              {language === 'fr' ? 'Délai de libération automatique écoulé' : 'Auto-release time has passed'}
            </p>
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              {language === 'fr'
                ? 'Les fonds seront libérés automatiquement'
                : 'Funds will be released automatically'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (!timeRemaining) return null;

  const { hours, minutes, seconds } = timeRemaining;
  const totalHours = hours + minutes / 60;
  const totalMinutes = hours * 60 + minutes;
  const isUrgent = totalHours < 12;
  const isWarning = totalHours < 24;
  const progress = Math.max(0, Math.min(100, ((72 - totalHours) / 72) * 100));

  return (
    <Card
      variant="outlined"
      className={`${
        isUrgent
          ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700'
          : isWarning
          ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-300 dark:border-orange-700'
          : 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700'
      } animate-fadeInUp`}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
              isUrgent
                ? 'from-red-500 to-pink-500'
                : isWarning
                ? 'from-orange-500 to-red-500'
                : 'from-blue-500 to-cyan-500'
            } flex items-center justify-center text-2xl shadow-lg animate-pulse`}
          >
            ⏱️
          </div>
          <div className="flex-1">
            <p
              className={`font-black text-lg mb-1 ${
                isUrgent
                  ? 'text-red-800 dark:text-red-200'
                  : isWarning
                  ? 'text-orange-800 dark:text-orange-200'
                  : 'text-blue-800 dark:text-blue-200'
              }`}
            >
              {language === 'fr' ? 'Libération automatique dans' : 'Auto-release in'}:
            </p>
            <div className="flex items-baseline gap-2">
              <span
                className={`text-3xl font-black ${
                  isUrgent
                    ? 'text-red-600 dark:text-red-400'
                    : isWarning
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}
              >
                {String(hours).padStart(2, '0')}:
                {String(minutes).padStart(2, '0')}:
                {String(seconds).padStart(2, '0')}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {hours} {language === 'fr' ? 'heures' : 'hours'} {minutes}{' '}
              {language === 'fr' ? 'minutes restantes' : 'minutes remaining'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <ProgressBar
            value={progress}
            variant={isUrgent ? 'error' : isWarning ? 'warning' : 'info'}
            animated
            showLabel
            label={`${progress.toFixed(1)}% ${language === 'fr' ? 'écoulé' : 'elapsed'}`}
          />
        </div>

        <p
          className={`text-xs font-medium ${
            isUrgent
              ? 'text-red-600 dark:text-red-400'
              : isWarning
              ? 'text-orange-600 dark:text-orange-400'
              : 'text-blue-600 dark:text-blue-400'
          }`}
        >
          {language === 'fr'
            ? 'Les fonds seront libérés automatiquement si aucune action n\'est prise'
            : 'Funds will auto-release if no action is taken'}
        </p>
      </div>
    </Card>
  );
};

export default AutoReleaseCountdown;
